import type {
    PatientRetrievalDto,
    PatientSummaryDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import { wireLookup, type SyncStatusTone } from './patientListModel';

/**
 * Where one requested series stands, as the connector states it.
 *
 * `queued` has not been asked of the source yet and is the only state that can be cancelled.
 * `downloading` has been asked for and runs to its end. `failed` and `cancelled` can be asked for
 * again; `notExportable` cannot, because the source itself cannot produce the object, and
 * `emptyAtSource` cannot, because the source lists the series but holds nothing in it.
 */
export type RetrievalProgressState =
    | 'queued'
    | 'downloading'
    | 'ready'
    | 'failed'
    | 'notExportable'
    | 'emptyAtSource'
    | 'cancelled';

/** One requested series, validated from the connector's record. */
export type SeriesRetrieval = {
    retrievalId: string;
    seriesInstanceUid: string;
    modality?: string;
    seriesDescription?: string;
    state: RetrievalProgressState;
    failureReason?: string;
    received: number;
    expected?: number;
    requestedAt: string;
    canRetry: boolean;
    canCancel: boolean;
};

const STATE_BY_WIRE = {
    Queued: 'queued',
    Downloading: 'downloading',
    Ready: 'ready',
    Failed: 'failed',
    NotExportable: 'notExportable',
    EmptyAtSource: 'emptyAtSource',
    Cancelled: 'cancelled',
} as const satisfies Record<string, RetrievalProgressState>;

/**
 * The connector's state name read as a {@link RetrievalProgressState}, or undefined for one this
 * client does not know. Every reader of a retrieval's state parses it here, so no reader re-derives
 * a state from the record's outcome and reason tokens.
 */
export function retrievalProgressStateOf(
    wire: string | null | undefined
): RetrievalProgressState | undefined {
    return wireLookup(STATE_BY_WIRE, wire);
}

/**
 * The connector's records reduced to what the panel draws. A record without a handle or with a
 * state this client does not know is dropped rather than guessed at: drawn under a guessed state,
 * it could offer an action that does not apply to it.
 */
export function seriesRetrievals(
    records: readonly PatientRetrievalDto[] | undefined
): SeriesRetrieval[] {
    return (records ?? []).flatMap((record) => {
        const state = retrievalProgressStateOf(record.state);
        if (
            !record.retrievalId ||
            !record.seriesInstanceUid ||
            !record.requestedAt ||
            state === undefined
        ) {
            return [];
        }
        return [
            {
                retrievalId: record.retrievalId,
                seriesInstanceUid: record.seriesInstanceUid,
                modality: record.modality ?? undefined,
                seriesDescription: record.seriesDescription ?? undefined,
                state,
                failureReason: record.failureReason ?? undefined,
                received: record.receivedInstanceCount ?? 0,
                expected: record.expectedInstanceCount ?? undefined,
                requestedAt: record.requestedAt,
                canRetry: record.canRetry === true,
                canCancel: record.canCancel === true,
            },
        ];
    });
}

/**
 * Display order: what is moving, then what is waiting, then what needs a decision, then what is
 * settled. Within a state the request order is kept, so a list being polled never reshuffles rows
 * that have not changed.
 */
const STATE_ORDER: Readonly<Record<RetrievalProgressState, number>> = {
    downloading: 0,
    queued: 1,
    failed: 2,
    cancelled: 3,
    notExportable: 4,
    emptyAtSource: 5,
    ready: 6,
};

export function orderedRetrievals(
    retrievals: readonly SeriesRetrieval[]
): SeriesRetrieval[] {
    return retrievals
        .map((retrieval, index) => ({ retrieval, index }))
        .sort(
            (a, b) =>
                STATE_ORDER[a.retrieval.state] -
                    STATE_ORDER[b.retrieval.state] || a.index - b.index
        )
        .map(({ retrieval }) => retrieval);
}

export type RetrievalSummary = Readonly<
    Record<RetrievalProgressState, number>
> & {
    total: number;
    /** Something is still queued or downloading, so the record will change without anyone acting. */
    active: boolean;
};

export function summariseRetrievals(
    retrievals: readonly SeriesRetrieval[]
): RetrievalSummary {
    const counts: Record<RetrievalProgressState, number> = {
        queued: 0,
        downloading: 0,
        ready: 0,
        failed: 0,
        notExportable: 0,
        emptyAtSource: 0,
        cancelled: 0,
    };
    for (const retrieval of retrievals) {
        counts[retrieval.state] += 1;
    }
    return {
        ...counts,
        total: retrievals.length,
        active: counts.queued + counts.downloading > 0,
    };
}

/** The handles of every retrieval that can still be cancelled. */
export function cancellableRetrievalIds(
    retrievals: readonly SeriesRetrieval[]
): string[] {
    return retrievals.filter((r) => r.canCancel).map((r) => r.retrievalId);
}

/**
 * A patient row's import at a glance: series still queued or downloading, and those that failed.
 * What the source caused (an object it cannot export, a series with nothing in it) and a cancel the
 * user chose are not counted — the connector leaves them out, and neither is something to attend to.
 */
export function patientImportActivity(patient: PatientSummaryDto): {
    inFlight: number;
    failed: number;
} {
    return {
        inFlight: Math.max(0, patient.retrievalsInFlight ?? 0),
        failed: Math.max(0, patient.retrievalsFailed ?? 0),
    };
}

/**
 * Dot tone per state, shared with the datasets' own retrieval dots so the two read alike. An object
 * the source cannot export, or a series it holds nothing in, is drawn in the neutral tone rather than
 * as an error: nothing failed on this side, and an error mark sends the reader looking for a fault here.
 */
const RETRIEVAL_TONES = {
    queued: 'pending',
    downloading: 'downloading',
    ready: 'downloaded',
    failed: 'error',
    notExportable: 'pending',
    emptyAtSource: 'pending',
    cancelled: 'pending',
} as const satisfies Record<RetrievalProgressState, SyncStatusTone>;

export function retrievalTone(state: RetrievalProgressState): SyncStatusTone {
    return RETRIEVAL_TONES[state];
}

/** How often a list or panel with an import in flight re-reads the connector's record. */
export const IMPORT_PROGRESS_POLL_MS = 2_000;
